import { liveQuery, type Subscription } from 'dexie';
import type { ProgressionState } from '@selflevel/domain';
import { db } from './db';
import { computeLocalProgression } from './selectors';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA PROGRESIÓN, CALCULADA UNA VEZ PARA TODA LA APP.
 *
 * La doctrina de `selectors.ts` es «derivar, no guardar»: la progresión se
 * recalcula desde el historial entero en cada lectura, y es una buena decisión
 * —no hay dos copias de un contador que puedan discrepar—. Lo que no estaba en
 * la doctrina es recalcularla VARIAS VECES por el mismo cambio.
 *
 * Y eso es lo que pasaba. `useProgression` era un `useLiveQuery` por
 * componente, y cada uno lee siete tablas enteras y recorre toda la historia.
 * `Acontecimientos` vive montado en el armazón, así que siempre había uno; la
 * pantalla —Hoy, Progreso, Perfil, Revisión— ponía otro; y en Progreso el mapa
 * de calor ponía un tercero para leer solo `byDay`. **Cada casilla marcada
 * disparaba dos o tres recorridos completos del historial**, exactamente
 * iguales, en la pantalla donde más se marca.
 *
 * Aquí hay UNA consulta viva, con cuantos oyentes haga falta. Los hooks leen su
 * último valor con `useSyncExternalStore`, que es la forma que tiene React de
 * leer un almacén externo sin rasgarse entre renders.
 *
 * El parámetro —el día lógico— sale de los ajustes, que son los mismos para
 * toda la app. Si cambia, se reconecta y la anterior se suelta. El suelo de
 * nivel se lee dentro de la propia consulta (ver `leerSuelo`).
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** El último nivel general enseñado en este dispositivo (lo anota `Acontecimientos`). */
export const NIVEL_VISTO = 'nivelVisto';

let clave: string | null = null;
let suscripcion: Subscription | null = null;
let valor: ProgressionState | undefined;
const oyentes = new Set<() => void>();
let apagado: ReturnType<typeof setTimeout> | null = null;

function avisar(): void {
  for (const oyente of oyentes) oyente();
}

/**
 * Si hay cuenta vinculada y la descarga inicial aún no ha terminado. Mientras
 * tanto el almacén está a medias: ni se sella nada ni se celebra nada con él.
 */
export async function bootstrapPendiente(): Promise<boolean> {
  const [cuenta, hecho] = await Promise.all([
    db.meta.get('syncAccountId'),
    db.meta.get('bootstrapHecho'),
  ]);
  const vinculada = typeof cuenta?.value === 'string' && cuenta.value.length > 0;
  return vinculada && hecho?.value !== true;
}

/**
 * EL SUELO DEL NIVEL (PRD 8.4): el mayor de lo guardado aquí, lo que sella el
 * servidor en el ledger y el último nivel que se le enseñó en este dispositivo.
 */
async function leerSuelo(): Promise<{ suelo: number; guardado: number }> {
  const [ajustes, ledger, visto] = await Promise.all([
    db.settings.get('unica'),
    db.ledger.toArray(),
    db.meta.get(NIVEL_VISTO),
  ]);
  // `Number(...) || 0`: un ajuste de una versión vieja puede no traer el campo.
  const guardado = Number(ajustes?.levelFloor) || 0;
  let suelo = guardado;
  for (const fila of ledger)
    if (Number.isFinite(fila.levelFloor)) suelo = Math.max(suelo, fila.levelFloor);
  if (typeof visto?.value === 'number' && Number.isFinite(visto.value)) {
    suelo = Math.max(suelo, visto.value);
  }
  return { suelo, guardado };
}

/**
 * Sube el suelo guardado, nunca lo baja. Directo a la tabla y no por
 * `updateSettings`: el suelo no viaja en `/ajustes`, así que marcarlos sucios
 * solo provocaría una subida inútil.
 */
async function subirSuelo(nivel: number): Promise<void> {
  await db.transaction('rw', db.settings, async () => {
    const ajustes = await db.settings.get('unica');
    if (ajustes && (Number(ajustes.levelFloor) || 0) < nivel)
      await db.settings.update('unica', { levelFloor: nivel });
  });
}

async function calcular(
  today: string,
): Promise<{ estado: ProgressionState; guardado: number; pendiente: boolean }> {
  const [sealed, { suelo, guardado }, pendiente] = await Promise.all([
    db.milestones.toArray().then((filas) => filas.map((m) => m.key)),
    leerSuelo(),
    bootstrapPendiente(),
  ]);
  const estado = await computeLocalProgression({
    today,
    levelFloor: suelo,
    sealedMilestones: sealed,
  });
  return { estado, guardado, pendiente };
}

/** La progresión recién calculada, fuera de la consulta viva (misma receta). */
export async function progresionAhora(today: string): Promise<ProgressionState> {
  return (await calcular(today)).estado;
}

/** Abre (o reutiliza) la consulta viva para este día. */
export function conectarProgresion(today: string): void {
  const nueva = today;
  if (nueva === clave && suscripcion) return;

  suscripcion?.unsubscribe();
  clave = nueva;
  // El suelo se lee DENTRO de la consulta viva: si llega el ledger por pull o
  // sube el suelo guardado, se recalcula sola, sin reconectar.
  suscripcion = liveQuery(() => calcular(today)).subscribe({
    next: ({ estado, guardado, pendiente }) => {
      valor = estado;
      avisar();
      // Con la descarga a medias la media de áreas puede salir inflada (llegó un
      // área y las demás no): no se sella un suelo con eso. Solo sube, así que
      // la reemisión que provoca la escritura ya no vuelve a escribir.
      if (!pendiente && estado.generalLevel > guardado) {
        void subirSuelo(estado.generalLevel).catch((error: unknown) =>
          console.error('[progresion] suelo', error),
        );
      }
    },
    // Un fallo de lectura no tumba la app: se deja el último valor bueno y se
    // anota. La siguiente escritura en cualquiera de las tablas lo reintenta.
    error: (error: unknown) => console.error('[progresion]', error),
  });
}

/**
 * Para `useSyncExternalStore`. Cuando se va el último oyente la consulta NO se
 * corta en seco: al cambiar de pantalla la vieja se desmonta antes de que la
 * nueva se monte, y cortar en ese hueco obligaría a recalcular el historial
 * entero desde cero un fotograma después. Se espera un segundo, y si nadie ha
 * vuelto, se suelta.
 */
export function suscribirProgresion(oyente: () => void): () => void {
  if (apagado) {
    clearTimeout(apagado);
    apagado = null;
  }
  oyentes.add(oyente);

  return () => {
    oyentes.delete(oyente);
    if (oyentes.size > 0) return;
    apagado = setTimeout(() => {
      apagado = null;
      if (oyentes.size > 0) return;
      suscripcion?.unsubscribe();
      suscripcion = null;
      clave = null;
      valor = undefined;
    }, 1_000);
  };
}

export function leerProgresion(): ProgressionState | undefined {
  return valor;
}
